import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';
import { NotificationsPanel } from '@decisiv/ui-components';

import useAccountAccess from './useAccountAccess';

const ACTIVE_A = {
  id: 1,
  name: 'Account A',
  roles: ['Member'],
  removedAt: null,
};
const ACTIVE_B = {
  id: 2,
  name: 'Account B',
  roles: ['Member'],
  removedAt: null,
};
const REMOVED_C = {
  id: 3,
  name: 'Account C',
  roles: ['Member'],
  removedAt: '2025-01-01T00:00:00Z',
};

let hook;

function Probe({ accountAccess }) {
  hook = useAccountAccess(7, accountAccess);

  return (
    <div>
      <span data-testid="rows">
        {hook.accounts.map((row) => row.id).join(',')}
      </span>
      <span data-testid="pending">{hook.pendingAccount?.id ?? ''}</span>
      <span data-testid="action">{hook.pendingAction ?? ''}</span>
      <span data-testid="error">{hook.modalError ?? ''}</span>
      <span data-testid="terminal">{String(hook.isTerminal)}</span>
      <span data-testid="can-remove-1">{String(hook.canActOn(ACTIVE_A))}</span>
      <button type="button" onClick={() => hook.openRemoveModal(ACTIVE_A)}>
        open remove
      </button>
      <button type="button" onClick={() => hook.openRestoreModal(REMOVED_C)}>
        open restore
      </button>
      <button type="button" onClick={() => hook.closeModal()}>
        close
      </button>
      <button type="button" onClick={() => hook.confirm()}>
        confirm
      </button>
    </div>
  );
}

const respondWith = ({ ok = true, status = 200, body = {} }) => {
  global.fetch = jest
    .fn()
    .mockResolvedValue({ ok, status, json: async () => body });
};

const renderProbe = (accountAccess) =>
  render(
    <ConfigProvider>
      <Probe accountAccess={accountAccess} />
      <NotificationsPanel marginTop={4} zIndex={1000} />
    </ConfigProvider>,
  );

beforeEach(() => {
  document.head.innerHTML = '<meta name="csrf-token" content="a-token" />';
});

afterEach(() => {
  hook = undefined;
  document.head.innerHTML = '';
  delete global.fetch;
});

describe('management/EditUser useAccountAccess', () => {
  it('picks up a later accountAccess from the parent', () => {
    const { rerender } = renderProbe([ACTIVE_A]);

    rerender(
      <ConfigProvider>
        <Probe accountAccess={[ACTIVE_A, ACTIVE_B]} />
        <NotificationsPanel marginTop={4} zIndex={1000} />
      </ConfigProvider>,
    );

    expect(screen.getByTestId('rows')).toHaveTextContent('1,2');
  });

  describe('canActOn', () => {
    it('disables Remove on the only active account', () => {
      renderProbe([ACTIVE_A, REMOVED_C]);

      expect(screen.getByTestId('can-remove-1')).toHaveTextContent('false');
    });

    it('allows Remove when another active account remains', () => {
      renderProbe([ACTIVE_A, ACTIVE_B]);

      expect(screen.getByTestId('can-remove-1')).toHaveTextContent('true');
    });

    it('always allows acting on an already-removed row', () => {
      renderProbe([ACTIVE_A, REMOVED_C]);

      expect(hook.canActOn(REMOVED_C)).toBe(true);
    });
  });

  it('opens the remove modal for the chosen row', () => {
    renderProbe([ACTIVE_A, ACTIVE_B]);

    userEvent.click(screen.getByText('open remove'));

    expect(screen.getByTestId('pending')).toHaveTextContent('1');
    expect(screen.getByTestId('action')).toHaveTextContent('remove');
  });

  it('opens the restore modal for the chosen row', () => {
    renderProbe([ACTIVE_A, REMOVED_C]);

    userEvent.click(screen.getByText('open restore'));

    expect(screen.getByTestId('pending')).toHaveTextContent('3');
    expect(screen.getByTestId('action')).toHaveTextContent('restore');
  });

  it('clears the pending action on close', () => {
    renderProbe([ACTIVE_A, ACTIVE_B]);

    userEvent.click(screen.getByText('open remove'));
    userEvent.click(screen.getByText('close'));

    expect(screen.getByTestId('pending')).toHaveTextContent('');
    expect(screen.getByTestId('action')).toHaveTextContent('');
  });

  describe('confirming a removal', () => {
    it('replaces the rows from the response, toasts, and closes the modal', async () => {
      respondWith({ body: { accountAccess: [ACTIVE_B, REMOVED_C] } });
      renderProbe([ACTIVE_A, ACTIVE_B]);

      userEvent.click(screen.getByText('open remove'));
      userEvent.click(screen.getByText('confirm'));

      expect(
        await screen.findByText('Account access removed.'),
      ).toBeInTheDocument();
      expect(screen.getByTestId('rows')).toHaveTextContent('2,3');
      expect(screen.getByTestId('pending')).toHaveTextContent('');
    });

    it('sends the pending row’s id to the remove endpoint', async () => {
      respondWith({ body: { accountAccess: [] } });
      renderProbe([ACTIVE_A, ACTIVE_B]);

      userEvent.click(screen.getByText('open remove'));
      userEvent.click(screen.getByText('confirm'));

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());
      expect(global.fetch).toHaveBeenCalledWith(
        '/management/users/7/remove_account_access',
        expect.objectContaining({ body: JSON.stringify({ account_id: 1 }) }),
      );
    });

    it('keeps the modal open, shows the message inline, and flags it terminal', async () => {
      respondWith({
        ok: false,
        status: 422,
        body: {
          errors: {
            base: ['This user must have access to at least one account.'],
          },
          terminal: true,
        },
      });
      renderProbe([ACTIVE_A, ACTIVE_B]);

      userEvent.click(screen.getByText('open remove'));
      userEvent.click(screen.getByText('confirm'));

      expect(await screen.findByTestId('error')).toHaveTextContent(
        'This user must have access to at least one account.',
      );
      expect(screen.getByTestId('terminal')).toHaveTextContent('true');
      expect(screen.getByTestId('pending')).toHaveTextContent('1');
      expect(
        screen.queryByText('Account access removed.'),
      ).not.toBeInTheDocument();
    });

    it('does not flag a non-terminal failure as terminal', async () => {
      respondWith({
        ok: false,
        status: 422,
        body: { errors: { base: ['Account not found.'] } },
      });
      renderProbe([ACTIVE_A, ACTIVE_B]);

      userEvent.click(screen.getByText('open remove'));
      userEvent.click(screen.getByText('confirm'));

      expect(await screen.findByTestId('error')).toHaveTextContent(
        'Account not found.',
      );
      expect(screen.getByTestId('terminal')).toHaveTextContent('false');
    });

    it('shows a generic message on a failure with nothing to say', async () => {
      respondWith({ ok: false, status: 500, body: {} });
      renderProbe([ACTIVE_A, ACTIVE_B]);

      userEvent.click(screen.getByText('open remove'));
      userEvent.click(screen.getByText('confirm'));

      expect(await screen.findByTestId('error')).toHaveTextContent(
        'Something went wrong. Please try again.',
      );
      expect(screen.getByTestId('terminal')).toHaveTextContent('false');
    });

    it('resets the terminal flag on close, not just the error', async () => {
      respondWith({
        ok: false,
        status: 422,
        body: { errors: { base: ['blocked'] }, terminal: true },
      });
      renderProbe([ACTIVE_A, ACTIVE_B]);

      userEvent.click(screen.getByText('open remove'));
      userEvent.click(screen.getByText('confirm'));
      await screen.findByTestId('error');
      expect(screen.getByTestId('terminal')).toHaveTextContent('true');

      userEvent.click(screen.getByText('close'));
      userEvent.click(screen.getByText('open remove'));

      expect(screen.getByTestId('terminal')).toHaveTextContent('false');
    });
  });

  describe('confirming a restore', () => {
    it('replaces the rows from the response and toasts', async () => {
      respondWith({
        body: {
          accountAccess: [
            ACTIVE_A,
            ACTIVE_B,
            { ...REMOVED_C, removedAt: null },
          ],
        },
      });
      renderProbe([ACTIVE_A, ACTIVE_B, REMOVED_C]);

      userEvent.click(screen.getByText('open restore'));
      userEvent.click(screen.getByText('confirm'));

      expect(
        await screen.findByText('Account access restored.'),
      ).toBeInTheDocument();
      expect(screen.getByTestId('rows')).toHaveTextContent('1,2,3');
    });
  });
});
